import { ChevronLeft, ChevronRight, Lock as LockIcon, Today } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { formatDisplayDate, formatDisplayDateTime } from "utils/dateFormat";
import { useMemo, useState } from "react";
import {
  clientDisplayLabel,
  dateKey,
  formatAppointmentTimeDisplay,
  groupAppointmentsByDate,
  isPrivateAppointment,
  parseApptDate,
  participantNames,
  statusBgColor,
  statusTextColor,
} from "./appointmentUtils";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAYS_SHORT = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i,
  label: format(new Date(2000, i, 1), "MMMM"),
}));

const buildYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
};

const TooltipRow = ({ label, children }) => {
  if (!children) return null;
  return (
    <Box>
      <Typography
        component="span"
        variant="caption"
        sx={{ display: "block", color: "rgba(255,255,255,0.7)", fontWeight: 700 }}
      >
        {label}
      </Typography>
      <Typography component="span" variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {children}
      </Typography>
    </Box>
  );
};

const AppointmentTooltip = ({ appt }) => {
  const parsedDate = parseApptDate(appt);
  const appointmentDate = formatDisplayDate(parsedDate, null);
  const reminderSent = appt.reminderSentAt
    ? formatDisplayDateTime(appt.reminderSentAt)
    : "Not sent";

  return (
    <Box sx={{ maxWidth: 320 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {appt.title || "Appointment"}
      </Typography>
      <Stack spacing={0.75}>
        <TooltipRow label={participantNames(appt).length > 1 ? "CLIENTS" : "CLIENT"}>
          {participantNames(appt).length > 1
            ? participantNames(appt).join(", ")
            : [appt.clientName, appt.clientEmail, appt.clientPhoneNumber]
                .filter(Boolean)
                .join(" · ")}
        </TooltipRow>
        <TooltipRow label="STATUS">{appt.status}</TooltipRow>
        <TooltipRow label="DATE / TIME">
          {[appointmentDate, formatAppointmentTimeDisplay(appt)].filter(Boolean).join(" · ")}
        </TooltipRow>
        <TooltipRow label="WITH">
          {[appt.withUserName, appt.withUserEmail].filter(Boolean).join(" · ")}
        </TooltipRow>
        <TooltipRow label="LOCATION">{appt.location}</TooltipRow>
        <TooltipRow label="MEET LINK">{appt.meetLink}</TooltipRow>
        <TooltipRow label="CC">{appt.ccEmails}</TooltipRow>
        <TooltipRow label="REMINDER SENT">{reminderSent}</TooltipRow>
        <TooltipRow label="DESCRIPTION">{appt.description}</TooltipRow>
        <TooltipRow label="NOTES">{appt.notes}</TooltipRow>
      </Stack>
    </Box>
  );
};

const CompactDayAppointments = ({ dayAppts, onSelectAppointment }) => {
  if (!dayAppts.length) return null;

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.25} sx={{ mt: 0.25 }}>
      {dayAppts.slice(0, 3).map((appt) => (
        <Box
          key={appt.id}
          onClick={(e) => {
            e.stopPropagation();
            onSelectAppointment?.(appt);
          }}
          title={appt.title}
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: statusTextColor[appt.status] || "#334155",
            cursor: "pointer",
          }}
        />
      ))}
      {dayAppts.length > 3 && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ fontSize: 9, fontWeight: 700, lineHeight: 1, cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            onSelectAppointment?.(dayAppts[3]);
          }}
        >
          +{dayAppts.length - 3}
        </Typography>
      )}
    </Stack>
  );
};

const AppointmentsCalendar = ({ appointments = [], isLoading, onSelectAppointment }) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));

  const yearOptions = useMemo(() => {
    const years = new Set(buildYearOptions());
    appointments.forEach((a) => {
      const d = parseApptDate(a);
      if (d) years.add(d.getFullYear());
    });
    years.add(currentMonth.getFullYear());
    return [...years].sort((a, b) => a - b);
  }, [appointments, currentMonth]);

  const handleMonthChange = (e) => {
    const month = Number(e.target.value);
    setCurrentMonth((prev) => startOfMonth(new Date(prev.getFullYear(), month, 1)));
  };

  const handleYearChange = (e) => {
    const year = Number(e.target.value);
    setCurrentMonth((prev) => startOfMonth(new Date(year, prev.getMonth(), 1)));
  };

  const byDate = useMemo(() => groupAppointmentsByDate(appointments), [appointments]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = [];
    let day = gridStart;
    while (day <= gridEnd) {
      days.push(day);
      day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
    }
    return days;
  }, [currentMonth]);

  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [...appointments]
      .filter((a) => {
        const d = parseApptDate(a);
        return d && d >= today && a.status === "SCHEDULED";
      })
      .sort((a, b) => {
        const da = parseApptDate(a)?.getTime() ?? 0;
        const db = parseApptDate(b)?.getTime() ?? 0;
        if (da !== db) return da - db;
        return (a.appointmentTime || "").localeCompare(b.appointmentTime || "");
      })
      .slice(0, 12);
  }, [appointments]);

  const weekdayLabels = isCompact ? WEEKDAYS_SHORT : WEEKDAYS;

  return (
    <Box className="flex flex-col xl:flex-row gap-4 min-w-0 w-full">
      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          spacing={1}
          sx={{ px: { xs: 1, sm: 2 }, py: 1.5, borderBottom: 1, borderColor: "divider" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            spacing={0.5}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <IconButton size="small" onClick={() => setCurrentMonth((m) => subMonths(m, 1))}>
              <ChevronLeft />
            </IconButton>
            <IconButton size="small" onClick={() => setCurrentMonth((m) => addMonths(m, 1))}>
              <ChevronRight />
            </IconButton>
            <Tooltip title="Go to today">
              <IconButton size="small" onClick={() => setCurrentMonth(startOfMonth(new Date()))}>
                <Today fontSize="small" />
              </IconButton>
            </Tooltip>
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: 0, sm: 130 },
                flex: { xs: "1 1 120px", sm: "0 0 auto" },
                ml: { xs: 0, sm: 0.5 },
              }}
            >
              <Select
                value={currentMonth.getMonth()}
                onChange={handleMonthChange}
                sx={{ fontWeight: 600, fontSize: { xs: 13, sm: 14 } }}
              >
                {MONTH_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: 0, sm: 96 },
                flex: { xs: "1 1 88px", sm: "0 0 auto" },
              }}
            >
              <Select
                value={currentMonth.getFullYear()}
                onChange={handleYearChange}
                sx={{ fontWeight: 600, fontSize: { xs: 13, sm: 14 } }}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {appointments.length} appointment{appointments.length === 1 ? "" : "s"}
          </Typography>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              {weekdayLabels.map((wd) => (
                <Typography
                  key={wd}
                  variant="caption"
                  sx={{
                    py: { xs: 0.5, md: 1 },
                    textAlign: "center",
                    fontWeight: 700,
                    color: "text.secondary",
                    fontSize: { xs: 10, sm: 12 },
                  }}
                >
                  {wd}
                </Typography>
              ))}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                gridAutoRows: { xs: "minmax(56px, auto)", md: "minmax(100px, auto)" },
              }}
            >
              {calendarDays.map((day) => {
                const key = dateKey(day);
                const dayAppts = byDate[key] || [];
                const inMonth = isSameMonth(day, currentMonth);
                const today = isToday(day);

                return (
                  <Box
                    key={key}
                    sx={{
                      minHeight: { xs: 56, md: 100 },
                      minWidth: 0,
                      p: { xs: 0.25, sm: 0.5, md: 0.75 },
                      borderRight: 1,
                      borderBottom: 1,
                      borderColor: "divider",
                      bgcolor: inMonth ? "background.paper" : "action.hover",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: { xs: 20, md: 24 },
                        height: { xs: 20, md: 24 },
                        borderRadius: "50%",
                        fontWeight: today ? 700 : 500,
                        fontSize: { xs: 10, md: 12 },
                        bgcolor: today ? "primary.main" : "transparent",
                        color: today
                          ? "primary.contrastText"
                          : inMonth
                          ? "text.secondary"
                          : "text.disabled",
                      }}
                    >
                      {format(day, "d")}
                    </Typography>
                    {isCompact ? (
                      <CompactDayAppointments
                        dayAppts={dayAppts}
                        onSelectAppointment={onSelectAppointment}
                      />
                    ) : (
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {dayAppts.slice(0, 3).map((appt) => (
                          <Tooltip
                            key={appt.id}
                            title={<AppointmentTooltip appt={appt} />}
                            arrow
                            placement="top"
                          >
                            <Box
                              onClick={() => onSelectAppointment?.(appt)}
                              sx={{
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                cursor: "pointer",
                                fontSize: 11,
                                lineHeight: 1.3,
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                bgcolor: statusBgColor[appt.status] || "#f1f5f9",
                                color: statusTextColor[appt.status] || "#334155",
                                "&:hover": { filter: "brightness(0.95)" },
                              }}
                            >
                              {formatAppointmentTimeDisplay(appt)
                                ? `${formatAppointmentTimeDisplay(appt)} `
                                : ""}
                              {isPrivateAppointment(appt) && (
                                <LockIcon
                                  sx={{ fontSize: 11, mr: 0.25, verticalAlign: "text-bottom" }}
                                />
                              )}
                              {appt.title}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  display: { xs: "none", lg: "block" },
                                  fontSize: 10,
                                  color: "inherit",
                                  opacity: 0.75,
                                }}
                              >
                                Client: {clientDisplayLabel(appt)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ))}
                        {dayAppts.length > 3 && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{ cursor: "pointer", fontWeight: 600, pl: 0.5 }}
                            onClick={() => onSelectAppointment?.(dayAppts[3])}
                          >
                            +{dayAppts.length - 3} more
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          width: { xs: "100%", xl: 300 },
          flexShrink: 0,
          minWidth: 0,
          order: { xs: -1, xl: 0 },
        }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1rem" } }}
          >
            Upcoming
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Scheduled appointments from today
          </Typography>
        </Box>
        <Stack spacing={0} sx={{ maxHeight: { xs: 240, sm: 320, md: 520 }, overflow: "auto" }}>
          {upcoming.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No upcoming scheduled appointments.
            </Typography>
          ) : (
            upcoming.map((appt) => {
              const d = parseApptDate(appt);
              return (
                <Box
                  key={appt.id}
                  onClick={() => onSelectAppointment?.(appt)}
                  sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: 1.25,
                    cursor: "pointer",
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography variant="caption" color="primary" fontWeight={700}>
                    {d ? format(d, isCompact ? "d MMM" : "EEE, d MMM") : "—"}
                    {formatAppointmentTimeDisplay(appt)
                      ? ` · ${formatAppointmentTimeDisplay(appt)}`
                      : ""}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {isPrivateAppointment(appt) && (
                      <LockIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: "text-bottom" }} />
                    )}
                    {appt.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
                  >
                    {clientDisplayLabel(appt)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default AppointmentsCalendar;
